import React from 'react';
import { Edit2, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';
import type { ContextQuestion, QuestionAnswer } from '@/types/frontend';

interface QuestionsSummaryProps {
  questions: ContextQuestion[];
  answers: QuestionAnswer[];
  onEdit: (index: number) => void;
  onSubmit: () => void;
  submitting: boolean;
  canSubmit: boolean;
}

export const QuestionsSummary: React.FC<QuestionsSummaryProps> = ({
  questions,
  answers,
  onEdit,
  onSubmit,
  submitting,
  canSubmit
}) => {
  // Get missing required questions
  const missingRequiredQuestions = (questions || [])
    .filter(q => q.required && !answers.some(a => a.question_id === q.id))
    .map(q => q?.question || 'Question non disponible');
  
  // Get answer label for a question
  const getAnswerLabel = (questionId: string): string => {
    const answer = (answers || []).find(a => a?.question_id === questionId);
    if (!answer) return 'Non répondu';
    
    const question = (questions || []).find(q => q?.id === questionId);
    if (!question) return 'Question inconnue';
    
    const option = (question?.options || []).find(o => o?.id === answer.selected_option_id);
    return option?.label || 'Option inconnue';
  };
  
  // Get category label
  const getCategoryLabel = (category: string) => {
    switch (category.toUpperCase()) {
      case 'VERIFICATION':
        return 'Vérification';
      case 'TECHNIQUE':
        return 'Technique';
      case 'BUSINESS':
        return 'Stratégie';
      case 'CONTRAINTES':
        return 'Contraintes';
      case 'PRIORITES':
        return 'Priorités';
      default:
        return category;
    }
  };
  
  return (
    <Card className="w-full animate-fade-in">
      <CardHeader>
        <CardTitle className="text-2xl">Résumé des réponses</CardTitle>
        <CardDescription>
          Vérifiez vos réponses avant de générer les recommandations
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Missing required questions warning */}
        {missingRequiredQuestions.length > 0 && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">
                  Questions requises non répondues
                </h4>
                <ul className="text-sm text-yellow-700 list-disc pl-5 space-y-1">
                  {missingRequiredQuestions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* Answers by category */}
        {['VERIFICATION', 'TECHNIQUE', 'BUSINESS', 'CONTRAINTES', 'PRIORITES'].map(category => {
          const categoryQuestions = (questions || []).filter(q => q?.category?.toUpperCase() === category);
          if (categoryQuestions.length === 0) return null;
          
          return (
            <div key={category} className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">
                {getCategoryLabel(category)}
              </h3>
              
              {categoryQuestions.map((question, index) => {
                const questionIndex = (questions || []).findIndex(q => q?.id === question?.id);
                const isAnswered = (answers || []).some(a => a?.question_id === question?.id);
                
                return (
                  <div 
                    key={question?.id || index}
                    className={cn(
                      'p-4 rounded-lg border',
                      isAnswered ? 'border-gray-200' : 'border-red-200 bg-red-50'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          {question?.required && (
                            <Badge
                              variant={isAnswered ? 'outline' : 'danger'}
                              size="sm"
                              className="mr-2"
                            >
                              Requis
                            </Badge>
                          )}
                          <h4 className="font-medium text-gray-900">
                            {question?.question || 'Question non disponible'}
                          </h4>
                        </div>
                        
                        <div className="flex items-center mt-2">
                          {isAnswered ? (
                            <div className="flex items-center text-green-600">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              <span className="font-medium">
                                {getAnswerLabel(question?.id || '')}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center text-red-600">
                              <AlertCircle className="w-4 h-4 mr-2" />
                              <span>Non répondu</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 text-gray-500 hover:text-blue-600"
                        onClick={() => onEdit(questionIndex)}
                        icon={<Edit2 className="w-4 h-4" />}
                      >
                        Modifier
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
      
      <CardFooter className="flex justify-between border-t border-gray-100 pt-6">
        <Button
          variant="outline"
          onClick={() => onEdit((questions?.length || 1) - 1)}
        >
          Retour aux questions
        </Button>
        
        <Button
          variant="primary"
          onClick={onSubmit}
          loading={submitting}
          disabled={!canSubmit || submitting}
        >
          Générer les recommandations
        </Button>
      </CardFooter>
    </Card>
  );
};

export default QuestionsSummary;